'use client'

import React, { useEffect, useState } from 'react'
import { useLanguage } from '@/app/(frontend)/providers/LanguageProvider'
import { FiPlus, FiMinus } from 'react-icons/fi'
import { motion } from 'framer-motion'

interface FAQItem {
  question: string
  answer: string
}

interface FaqSectionProps {
  sectionId?: string
}

const extractTextFromLexical = (answer: any): string => {
  try {
    const children = answer?.root?.children ?? []
    return children
      .map((node: any) => {
        return node?.children?.map((child: any) => child?.text || '').join(' ') || ''
      })
      .join('\n')
  } catch {
    return ''
  }
}

// Normalizes a relatedSection field to a plain id string, regardless of shape:
// - a plain id string (legacy single-collection relation)
// - { relationTo, value: 'id' } (polymorphic, not depth-resolved)
// - { relationTo, value: { id: 'id', ... } } (polymorphic, depth-resolved)
const getRelatedId = (relatedSection: any): string | undefined => {
  if (!relatedSection) return undefined
  if (typeof relatedSection === 'string') return relatedSection
  if (typeof relatedSection.value === 'string') return relatedSection.value
  if (relatedSection.value?.id) return relatedSection.value.id
  return relatedSection.id
}

const FaqSection: React.FC<FaqSectionProps> = ({ sectionId }) => {
  const { language } = useLanguage()
  const isArabic = language === 'ar'

  const [faqData, setFaqData] = useState<FAQItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [openIndex, setOpenIndex] = useState<number | null>(0)

  const toggleIndex = (index: number) => {
    setOpenIndex(openIndex === index ? null : index)
  }

  useEffect(() => {
    const fetchFAQs = async () => {
      try {
        const res = await fetch(`/api/faq-items?depth=1&limit=200&locale=${language}`)
        if (!res.ok) throw new Error('Failed to fetch FAQ items')
        const data = await res.json()

        const mappedFaqs: FAQItem[] = data.docs
          .filter((item: any) => getRelatedId(item.relatedSection) === sectionId)
          .map((item: any) => ({
            question: item.question,
            answer: extractTextFromLexical(item.answer),
          }))

        setFaqData(mappedFaqs)
      } catch (err: any) {
        setError(err.message || 'An error occurred')
      } finally {
        setLoading(false)
      }
    }

    fetchFAQs()
  }, [sectionId, language])

  return (
    <section
      className="md:max-w-2xl lg:max-w-5xl xl:max-w-[1250px] 2xl:max-w-[1400px] mx-auto px-4 pt-12 pb-20 xl:px-0"
      dir={isArabic ? 'rtl' : 'ltr'}
    >
      <motion.h2
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        viewport={{ once: true }}
        className="why-heading text-[24px] md:text-[32px] xl:text-[38px] 2xl:text-[42px] font-[600] text-[#414141] mb-4 mt-36"
      >
        {isArabic ? 'الأسئلة الشائعة' : 'Frequently Asked Questions'}
      </motion.h2>

      <motion.p
        initial={{ opacity: 0, y: 25 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
        viewport={{ once: true }}
        className="md:max-w-md xl:max-w-2xl text-[15px] md:text-xs xl:text-base text-[#666666] mb-10 xl:mb-10 !leading-[1.1rem] mt-10"
      >
        {isArabic
          ? 'من خيارات التخزين إلى تفاصيل الخدمة، يغطي قسم الأسئلة الشائعة لدينا أبرز الأسئلة التي يطرحها العملاء قبل التعاون مع Container City®.'
          : 'From Storage Options to Service Details, Our FAQ Section Covers the Most Common Questions Clients Ask Before Partnering with Container City®.'}
      </motion.p>

      {loading && <p>{isArabic ? 'جاري تحميل الأسئلة...' : 'Loading FAQs...'}</p>}
      {error && <p className="text-red-500">{error}</p>}

      {!loading && !error && faqData.length > 0 && (
        <div className="divide-y divide-[#666666] border-b border-[#666666]">
          {faqData.map((item, index) => (
            <div key={index}>
              <button
                onClick={() => toggleIndex(index)}
                className="w-full flex justify-between items-center py-5 text-left"
              >
                <span className="text-[16px] font-medium text-[#414141]">{item.question}</span>
                <span className="text-2xl">{openIndex === index ? <FiMinus /> : <FiPlus />}</span>
              </button>
              {openIndex === index && (
                <div className="pb-5 text-gray-700 text-[15px] whitespace-pre-line">
                  {item.answer}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {!loading && !error && faqData.length === 0 && (
        <p className="text-gray-500">
          {isArabic ? 'لا توجد أسئلة متاحة حالياً.' : 'No FAQs available at the moment.'}
        </p>
      )}
    </section>
  )
}

export default FaqSection
